import { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Chip, CircularProgress, Alert, IconButton,
  Tooltip, Divider, SwipeableDrawer, ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TodayIcon from "@mui/icons-material/Today";

// ─── Config ───────────────────────────────────────────────────────────────────
const CALENDARS = {
  holidays: { label: "Holidays in Canada", color: "#ef4444" },
  tdsb:     { label: "TDSB",               color: "#3b82f6" },
};

// ─── TDSB Manual Events (hardcoded stopgap) ──────────────────────────────────
// TDSB's public Google Calendar feed (see api/calendar.js) isn't reflecting
// current school-year dates, so these are entered by hand for now. Remove
// individual entries once the live feed picks them up, or drop this whole
// block if the feed gets fixed.
const TDSB_MANUAL_EVENTS = [
  { summary: "Winter Break",                     start: "2026-12-21", end: "2027-01-01" },
  { summary: "Mid-Winter Break",                  start: "2027-03-15", end: "2027-03-19" },
  { summary: "Elementary & Secondary PA Day",     start: "2026-11-20" },
  { summary: "Elementary PA Day ONLY",            start: "2027-01-15" },
  { summary: "Secondary PA Day ONLY",             start: "2027-02-02" },
  { summary: "Elementary & Secondary PA Day",     start: "2027-02-12" },
  { summary: "Elementary PA Day ONLY",            start: "2027-06-04" },
  { summary: "Secondary PA Day ONLY",              start: "2027-06-29" },
  { summary: "Elementary & Secondary PA Day",     start: "2027-06-30" },
];

function buildTdsbManualEvents() {
  return TDSB_MANUAL_EVENTS.map((e, i) => {
    const end = e.end || e.start;
    const multiDay = end !== e.start;
    return {
      id: `tdsb-manual-${i}`,
      summary: e.summary,
      date: e.start,
      endDate: end,
      calendar: "tdsb",
      description: multiDay ? `Runs ${fmtFullDate(e.start)} – ${fmtFullDate(end)}` : null,
      multiDay,
    };
  });
}

const DAY_OPTIONS = [
  { label: "30d", days: 30 },
  { label: "60d", days: 60 },
  { label: "90d", days: 90 },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  if (d.getTime() === today.getTime()) return "Today";
  if (d.getTime() === tomorrow.getTime()) return "Tomorrow";
  return d.toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" });
}

function fmtFullDate(dateStr) {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-CA", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
}

function daysUntil(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + "T00:00:00");
  return Math.round((d - today) / 86400000);
}

// ─── Event card ───────────────────────────────────────────────────────────────
function EventCard({ event, dark, index, border }) {
  const [expanded, setExpanded] = useState(false);
  const cal = CALENDARS[event.calendar];
  const days = daysUntil(event.date);
  const isToday = days === 0;
  const isSoon = days <= 7;

  return (
    <Box onClick={() => setExpanded(e => !e)} sx={{
      display: "flex", alignItems: "stretch",
      borderBottom: `1px solid ${dark ? "#1e293b" : "#f1f5f9"}`,
      bgcolor: isToday
        ? (dark ? "#1e293b55" : "#f0fdf4")
        : dark ? (index % 2 === 0 ? "transparent" : "#ffffff08") : (index % 2 === 0 ? "#ffffff" : "#f8fafc"),
      cursor: "pointer", userSelect: "none",
    }}>
      {/* Left colour bar */}
      <Box sx={{ width: 3, bgcolor: cal?.color || "#64748b", flexShrink: 0 }} />

      <Box sx={{ flex: 1, px: 2, py: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Date column */}
          <Box sx={{ minWidth: 80, flexShrink: 0 }}>
            <Typography sx={{
              fontSize: "0.85rem", fontWeight: isToday ? 800 : 600,
              color: isToday ? "#10b981" : isSoon ? (dark ? "#f1f5f9" : "#0f172a") : "text.secondary",
            }}>
              {fmtDate(event.date)}
            </Typography>
            {event.time && (
              <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>{event.time}</Typography>
            )}
          </Box>

          {/* Event name */}
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "text.primary", flex: 1 }}>
            {event.summary}
          </Typography>

          {/* Calendar source chip */}
          <Chip
            label={cal?.label || event.calendarLabel}
            size="small"
            sx={{
              height: 20, fontSize: "0.68rem", fontWeight: 600, flexShrink: 0,
              bgcolor: `${cal?.color}22`,
              color: cal?.color,
              border: `1px solid ${cal?.color}44`,
            }}
          />
        </Box>

        {/* Expanded detail */}
        {expanded && (
          <Box sx={{ mt: 0.75, pl: "88px" }}>
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 0.25 }}>
              📅 {fmtFullDate(event.date)}{event.time ? ` · ${event.time}` : " · All day"}
            </Typography>
            {event.location && (
              <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 0.25 }}>
                📍 {event.location}
              </Typography>
            )}
            {event.description && (
              <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", lineHeight: 1.5 }}>
                {event.description}
              </Typography>
            )}
            {event.url && (
              <Box
                component="a"
                href={event.url}
                target="_blank"
                rel="noreferrer"
                onClick={e => e.stopPropagation()}
                sx={{
                  display: "inline-flex", alignItems: "center", gap: 0.5,
                  mt: 0.75, fontSize: "0.75rem", fontWeight: 600,
                  color: cal?.color || "#3b82f6",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                View event →
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─── CalendarPage ─────────────────────────────────────────────────────────────
export default function CalendarPage({ colorMode, setColorMode, dark, border, barBg, isMobile }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [daysFilter, setDaysFilter] = useState(30);
  const [calendarFilter, setCalendarFilter] = useState("all");

  const load = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/calendar");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setEvents([...data, ...buildTdsbManualEvents()].sort((a, b) => a.date.localeCompare(b.date)));
      setLastRefresh(new Date());
    } catch (e) {
      setError(`Failed to load calendar: ${e.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(() => load(), 60 * 60 * 1000); // refresh hourly
    return () => clearInterval(interval);
  }, [load]);

  // Filter by days and calendar source
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() + daysFilter);
  const cutoffStr = cutoff.toISOString().split("T")[0];

  const displayed = events
    .filter(e => e.date <= cutoffStr)
    .filter(e => calendarFilter === "all" || e.calendar === calendarFilter);

  const activeFilterCount = [daysFilter !== 30, calendarFilter !== "all"].filter(Boolean).length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

      {/* ── Header ── */}
      <Box sx={{ height: 52, display: "flex", alignItems: "center", px: 2, gap: 1, borderBottom: `1px solid ${border}`, bgcolor: barBg, flexShrink: 0 }}>
        <CalendarMonthIcon sx={{ fontSize: 20, color: "#8b5cf6", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>Calendar</Typography>
        {!isMobile && (
          <Chip label={`Next ${daysFilter} days`} size="small"
            sx={{ fontSize: "0.7rem", height: 20, bgcolor: dark ? "#1e293b" : "#f1f5f9", color: "text.secondary", fontWeight: 600, ml: 0.5 }} />
        )}
        <Box sx={{ flex: 1 }} />
        {displayed.length > 0 && (
          <Chip label={`${displayed.length} event${displayed.length !== 1 ? "s" : ""}`} size="small"
            sx={{ fontSize: "0.7rem", height: 20, bgcolor: dark ? "#1e293b" : "#f1f5f9", color: "text.secondary", fontWeight: 700 }} />
        )}
        {lastRefresh && (
          <Typography variant="caption" sx={{ color: "text.secondary", display: { xs: "none", sm: "block" }, fontSize: "0.72rem" }}>
            {lastRefresh.toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit", hour12: false })}
          </Typography>
        )}
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => load(true)} disabled={refreshing} sx={{ color: "text.secondary" }}>
            {refreshing ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Filters">
          <IconButton size="small" onClick={() => setFilterDrawerOpen(true)} sx={{ color: activeFilterCount > 0 ? "#8b5cf6" : "text.secondary" }}>
            <TuneIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Filter Drawer ── */}
      <SwipeableDrawer
        anchor={isMobile ? "bottom" : "right"}
        open={filterDrawerOpen}
        onOpen={() => setFilterDrawerOpen(true)}
        onClose={() => setFilterDrawerOpen(false)}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            bgcolor: barBg,
            borderTop: isMobile ? `1px solid ${border}` : "none",
            borderLeft: !isMobile ? `1px solid ${border}` : "none",
            borderRadius: isMobile ? "12px 12px 0 0" : 0,
            width: isMobile ? "100%" : 280,
            maxHeight: isMobile ? "80vh" : "100vh",
          }
        }}
      >
        <Box sx={{ px: 2.5, pt: isMobile ? 2 : 2.5, pb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${border}` }}>
          {isMobile && <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: border, position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)" }} />}
          <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Filters</Typography>
          <IconButton size="small" onClick={() => setFilterDrawerOpen(false)} sx={{ color: "text.secondary", mr: -0.5 }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
        <Box sx={{ px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 2.5, overflowY: "auto" }}>

          {/* Date range */}
          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mb: 1 }}>Date range</Typography>
            <ToggleButtonGroup value={daysFilter} exclusive size="small" fullWidth
              onChange={(_, v) => { if (v !== null) { setDaysFilter(v); setFilterDrawerOpen(false); } }}>
              {DAY_OPTIONS.map(o => (
                <ToggleButton key={o.label} value={o.days} sx={{ flex: 1 }}>{o.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Divider />

          {/* Calendar source */}
          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mb: 1 }}>Calendar</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box onClick={() => { setCalendarFilter("all"); setFilterDrawerOpen(false); }} sx={{
                display: "flex", alignItems: "center", gap: 1, px: 1.25, py: 0.9, borderRadius: 1, cursor: "pointer",
                bgcolor: calendarFilter === "all" ? (dark ? "#1e293b" : "#f1f5f9") : "transparent",
                border: `1px solid ${calendarFilter === "all" ? "#8b5cf6" : "transparent"}`,
              }}>
                <CalendarMonthIcon sx={{ fontSize: 16, color: "#8b5cf6" }} />
                <Typography sx={{ fontSize: "0.92rem", fontWeight: calendarFilter === "all" ? 700 : 400, color: calendarFilter === "all" ? "text.primary" : "text.secondary", flex: 1 }}>
                  All Calendars
                </Typography>
                <Chip label={events.filter(e => e.date <= cutoffStr).length} size="small"
                  sx={{ height: 20, fontSize: "0.7rem", bgcolor: dark ? "#0f172a" : "#e2e8f0", color: "text.secondary" }} />
              </Box>
              {Object.entries(CALENDARS).map(([id, cal]) => {
                const count = events.filter(e => e.calendar === id && e.date <= cutoffStr).length;
                const active = calendarFilter === id;
                return (
                  <Box key={id} onClick={() => { setCalendarFilter(id); setFilterDrawerOpen(false); }} sx={{
                    display: "flex", alignItems: "center", gap: 1, px: 1.25, py: 0.9, borderRadius: 1, cursor: "pointer",
                    bgcolor: active ? (dark ? "#1e293b" : "#f1f5f9") : "transparent",
                    border: `1px solid ${active ? cal.color : "transparent"}`,
                  }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: cal.color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "0.92rem", fontWeight: active ? 700 : 400, color: active ? "text.primary" : "text.secondary", flex: 1 }}>
                      {cal.label}
                    </Typography>
                    <Chip label={count} size="small"
                      sx={{ height: 20, fontSize: "0.7rem", bgcolor: dark ? "#0f172a" : "#e2e8f0", color: "text.secondary" }} />
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Divider />

          {/* Appearance */}
          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mb: 1 }}>Appearance</Typography>
            <ToggleButtonGroup value={colorMode} exclusive onChange={(_, v) => v && setColorMode(v)} size="small" fullWidth>
              <ToggleButton value="light" sx={{ flex: 1 }}>Light</ToggleButton>
              <ToggleButton value="dark" sx={{ flex: 1 }}>Dark</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </SwipeableDrawer>

      {error && <Alert severity="error" sx={{ mx: 2, mt: 1, fontSize: "0.82rem" }}>{error}</Alert>}

      {/* ── Content ── */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", bgcolor: "background.default" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress size={30} sx={{ color: "#8b5cf6" }} />
          </Box>
        ) : displayed.length === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "80%", gap: 1.5 }}>
            <TodayIcon sx={{ fontSize: 40, color: "#8b5cf6", opacity: 0.3 }} />
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>No events in the next {daysFilter} days</Typography>
          </Box>
        ) : (
          displayed.map((event, idx) => (
            <EventCard key={event.id} event={event} dark={dark} index={idx} border={border} />
          ))
        )}
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ height: 52, bgcolor: barBg, borderTop: `1px solid ${border}`, px: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>
          Holidays in Canada · TDSB
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#8b5cf6", animation: "pulse 2s infinite" }} />
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>Hourly</Typography>
        </Box>
      </Box>
    </Box>
  );
}
